// src/components/HCCConditionsList.jsx
import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Divider,
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  CircularProgress,
  Alert
} from '@mui/material';
import ListAltIcon from '@mui/icons-material/ListAlt';
import MedicationIcon from '@mui/icons-material/Medication';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { generateHccConditionsFromAI } from '../../../../utils/services/hccAiService';

const HCCConditionsList = ({
  clinicalNotes,
  medications,
  labs,
  onConditionsReady, // 👈 nuevo callback
}) => {
  const [infoOpen, setInfoOpen] = useState(false);
  const [conditions, setConditions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Limpia estado si cambian las notas y quedan vacías
  useEffect(() => {
    const normalizedNotes =
      Array.isArray(clinicalNotes)
        ? clinicalNotes.join('\n---\n')
        : clinicalNotes || '';

    if (!normalizedNotes || normalizedNotes.trim().length === 0) {
      setConditions([]);
      setError(null);
      setLoading(false);
      if (onConditionsReady) {
        onConditionsReady([]); // 👈 también limpiamos arriba
      }
    }
  }, [clinicalNotes, onConditionsReady]);

  const handleGenerate = async () => {
    // Normalizamos las notas para validar que haya contenido
    const normalizedNotes =
      Array.isArray(clinicalNotes)
        ? clinicalNotes.join('\n---\n')
        : clinicalNotes || '';

    if (!normalizedNotes || normalizedNotes.trim().length === 0) {
      setError('No clinical notes available for AI analysis.');
      setConditions([]);
      if (onConditionsReady) {
        onConditionsReady([]);
      }
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const aiConditions = await generateHccConditionsFromAI({
        clinicalNotes: normalizedNotes,
      });

      if (!aiConditions || aiConditions.length === 0) {
        console.warn(
          '[HCCConditionsList] AI devolvió vacío, sin condiciones HCC.'
        );
        setConditions([]);
        if (onConditionsReady) {
          onConditionsReady([]);
        }
      } else {
        setConditions(aiConditions);
        if (onConditionsReady) {
          onConditionsReady(aiConditions); // 👈 pasamos el resultado al padre
        }
      }
    } catch (err) {
      console.error('[HCCConditionsList] Error generando HCC:', err);
      setError('Error al generar las condiciones HCC con la IA.');
      setConditions([]);
      if (onConditionsReady) {
        onConditionsReady([]);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card elevation={3} sx={{ borderRadius: 2, mb: 4 }}>
        <CardHeader
          title="HCC Chronic Conditions List"
          subheader="AI-generated summary from clinical notes"
          titleTypographyProps={{ variant: 'h5', align: 'center' }}
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              <ListAltIcon fontSize="large" color="primary" />

              <Button
                variant="contained"
                size="small"
                onClick={handleGenerate}
                disabled={loading}
              >
                {loading ? 'Generating…' : 'Generate HCC with AI'}
              </Button>

              <Tooltip title="View data sources for this output">
                <IconButton
                  size="small"
                  onClick={() => setInfoOpen(true)}
                  sx={{
                    bgcolor: 'warning.light',
                    '&:hover': { bgcolor: 'warning.main' },
                    color: 'common.black',
                  }}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          }
        />
        <Divider />
        <CardContent>
          {loading && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 3,
              }}
            >
              <CircularProgress size={28} />
              <Typography variant="body2" sx={{ ml: 2 }}>
                Generating HCC chronic conditions with AI...
              </Typography>
            </Box>
          )}

          {!loading && error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!loading && !error && conditions.length === 0 && (
            <Typography variant="body2">
              No HCC data available for this patient based on current clinical notes.
            </Typography>
          )}

          {!loading && !error && conditions.length > 0 && (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant="subtitle2">HCC</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2">ICD-10</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2">Description</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="subtitle2">RAF</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2">
                      MEAT (Monitoring)
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2">Treatment</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {conditions.map((cond, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>{cond.hcc}</TableCell>
                    <TableCell>{cond.icd10}</TableCell>
                    <TableCell>{cond.description}</TableCell>
                    <TableCell align="center">
                      {cond.rafWeight != null &&
                      !Number.isNaN(Number(cond.rafWeight))
                        ? Number(cond.rafWeight).toFixed(2)
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          <strong>
                            {cond.meat?.monitoring?.date || 'N/A'}
                          </strong>{' '}
                          – {cond.meat?.monitoring?.source || 'N/A'} (
                          {cond.meat?.monitoring?.noteType || 'N/A'})
                        </Typography>
                        <Typography variant="caption">
                          {cond.meat?.monitoring?.provider || ''}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2">
                          <MedicationIcon
                            fontSize="small"
                            sx={{ verticalAlign: 'middle', mr: 0.5 }}
                          />
                          {cond.meat?.treatment?.medication || 'N/A'}
                        </Typography>
                        <Typography variant="caption">
                          {(cond.meat?.treatment?.date || 'N/A') +
                            ', ' +
                            (cond.meat?.treatment?.provider || '')}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Info modal: input data for this output */}
      <Dialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Input data used for this output</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" gutterBottom>
            Required clinical inputs
          </Typography>
          <ul>
            <li>
              <Typography variant="body2">
                <strong>Chronic diagnoses / HCC problems:</strong> active
                problems list and chronic ICD-10 codes mapped to HCC groups.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>ICD-10 → HCC / RAF mapping:</strong> configuration
                table that links each ICD-10 to its HCC category and RAF weight.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Clinical notes (PCP / specialists / consults):</strong>{' '}
                structured sections where Monitoring, Evaluation and Assessment
                are documented (for example, in the Assessment &amp; Plan
                section).
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Labs:</strong> key tests used as Monitoring evidence
                (e.g. HbA1c, TSH, renal function, lipid panel, etc.).
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Pharmacy / medication claims:</strong> prescriptions and
                fills used as Treatment evidence (drug name, dose, date, source,
                provider).
              </Typography>
            </li>
          </ul>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HCCConditionsList;
